import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { BudgetPlanDetail, BudgetPlanListItem, BudgetPlanRequest } from './budgetPlanTypes';

/**
 * The plan list files by fiscal year, by STAGE, and by the row's own state.
 *
 * `status` is the shell's reserved axis — active or deleted, which Show deleted lifts — and
 * `stage` is where the plan sits in the budget cycle. They were one key until 22 Sep 2026,
 * and the cost was not cosmetic: `<IndexPage>` sends `status=active` at rest, the server read
 * it as an unparseable lifecycle value, and the list matched nothing on every first load.
 */
export interface BudgetPlanListQuery extends ListQuery {
  fiscalYear?: string;
  stage?: string;
}

function toQueryString(query: BudgetPlanListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Both facets are sent whenever the KEY is present, empty string included — an empty value
    is the reader saying "none of these", which matches no row. A truthiness check would drop
    it and the server would read the axis as unmentioned, which is the opposite filter.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.fiscalYear !== undefined) params.set('fiscalYear', query.fiscalYear);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const budgetPlansApi = {
  clear: () => api<number>('/api/budget-plans/clear', { method: 'POST' }),

  list: (query: BudgetPlanListQuery, signal?: AbortSignal) =>
    api<PagedResult<BudgetPlanListItem>>(`/api/budget-plans${toQueryString(query)}`, { signal }),

  /** One plan with its Goal Setting resolved. */
  get: (id: string, signal?: AbortSignal) =>
    api<BudgetPlanDetail>(`/api/budget-plans/${id}`, { signal }),

  create: (request: BudgetPlanRequest) =>
    api<BudgetPlanDetail>('/api/budget-plans', { method: 'POST', body: request }),

  update: (id: string, request: BudgetPlanRequest) =>
    api<BudgetPlanDetail>(`/api/budget-plans/${id}`, { method: 'PUT', body: request }),

  /**
   * Opens the department submission window.
   *
   * Its own key — `budget-plans.manage-submission` — because declaring the year open invites
   * every department to file against the plan, which is a different decision from correcting
   * its description. Refused while the plan has adopted no goals.
   */
  openSubmission: (id: string) =>
    api<BudgetPlanDetail>(`/api/budget-plans/${id}/open-submission`, { method: 'POST' }),

  /** Closes the window. The plan then awaits consolidated review. Same key as opening it. */
  closeSubmission: (id: string) =>
    api<BudgetPlanDetail>(`/api/budget-plans/${id}/close-submission`, { method: 'POST' }),

  /** Soft delete. The Goal Setting links are left exactly as they are. */
  deactivate: (id: string) => api<void>(`/api/budget-plans/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/budget-plans/${id}/restore`, { method: 'POST' }),
};
